import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Inbox, Reply, Check } from "lucide-react";

function ContactCard({ m, onUpdate }) {
  const [replying, setReplying] = useState(false);
  const [reply, setReply] = useState("");
  const [busy, setBusy] = useState(false);

  const markRead = async () => {
    try { await api.adminContactRead(m.contact_id); onUpdate(m.contact_id, { read: true }); }
    catch { toast.error("Couldn't mark as read."); }
  };
  const sendReply = async () => {
    if (!reply.trim()) return toast.error("Write a reply first.");
    setBusy(true);
    try {
      const res = await api.adminContactReply(m.contact_id, reply.trim());
      toast.success(res.in_app ? "Reply emailed + delivered to their Welp inbox." : "Reply emailed.");
      onUpdate(m.contact_id, { read: true, replied: true });
      setReplying(false); setReply("");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't send the reply.");
    } finally { setBusy(false); }
  };

  return (
    <li className={`rounded-2xl border p-5 ${m.read ? "border-black/10 bg-white/70 backdrop-blur-lg" : "border-[#10B981]/40 bg-[#10B981]/5"}`} data-testid={`contact-msg-${m.contact_id}`}>
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <div className="text-sm font-bold text-[#1A1A1A]">{m.subject}</div>
          <div className="text-xs text-[#5A5A5A] mt-0.5">{m.name} · {m.email} · {new Date(m.created_at).toLocaleString()}</div>
        </div>
        <div className="flex gap-2 items-center">
          {m.replied && <span className="text-xs px-2.5 py-1 rounded-full bg-[#FF1A82]/15 text-[#FF1A82] border border-[#FF1A82]/30">Replied</span>}
          {!m.read && (
            <button onClick={markRead} className="text-xs text-[#FF1A82] hover:underline inline-flex items-center gap-1" data-testid={`contact-read-${m.contact_id}`}>
              <Check className="w-3.5 h-3.5" /> Mark read
            </button>
          )}
        </div>
      </div>
      <p className="text-sm text-[#3A3A3A] mt-3 whitespace-pre-line">{m.message}</p>
      {!replying ? (
        <button onClick={() => setReplying(true)} className="mt-3 text-xs uppercase tracking-[0.18em] text-[#10B981] font-semibold hover:underline inline-flex items-center gap-1" data-testid={`contact-reply-btn-${m.contact_id}`}>
          <Reply className="w-3.5 h-3.5" /> Reply
        </button>
      ) : (
        <div className="mt-3 space-y-2">
          <textarea value={reply} onChange={(e) => setReply(e.target.value)} maxLength={5000} rows={3}
            placeholder={`Reply to ${m.name.split(" ")[0]}…`}
            className="w-full rounded-xl border border-black/15 px-4 py-3 text-sm bg-white/70 backdrop-blur-lg text-[#1A1A1A] focus:outline-none focus:border-[#10B981] resize-y"
            data-testid={`contact-reply-text-${m.contact_id}`} />
          <div className="flex gap-2">
            <button onClick={sendReply} disabled={busy}
              className="px-5 py-2 rounded-full bg-[#10B981] text-white text-sm font-bold hover:brightness-110 disabled:opacity-60"
              data-testid={`contact-reply-send-${m.contact_id}`}>{busy ? "Sending…" : "Send reply"}</button>
            <button onClick={() => setReplying(false)} className="px-4 py-2 rounded-full text-sm text-[#5A5A5A] hover:text-[#1A1A1A]">Cancel</button>
          </div>
        </div>
      )}
    </li>
  );
}

export const AdminContactInbox = () => {
  const [items, setItems] = useState(null);

  useEffect(() => {
    api.adminContactMessages().then(d => setItems(d.messages || [])).catch(() => setItems([]));
  }, []);

  const onUpdate = (id, patch) => setItems(list => list.map(x => x.contact_id === id ? { ...x, ...patch } : x));
  const unread = (items || []).filter(m => !m.read).length;

  return (
    <div className="mt-6 rounded-2xl border border-black/10 bg-white/70 backdrop-blur-lg shadow-sm p-6" data-testid="admin-contact-inbox">
      <div className="flex items-center gap-2 text-[#1A1A1A]">
        <Inbox className="w-4 h-4 text-[#FF1A82]" />
        <h2 className="font-heading text-2xl">Contact inbox</h2>
        {unread > 0 && <span className="text-xs px-2.5 py-1 rounded-full bg-[#10B981] text-white font-bold" data-testid="contact-unread-count">{unread} new</span>}
      </div>
      {items === null ? (
        <p className="text-[#5A5A5A] text-sm mt-4">Loading…</p>
      ) : items.length === 0 ? (
        <p className="text-[#5A5A5A] text-sm mt-4">No contact messages yet. The public form lives at /contact.</p>
      ) : (
        <ul className="mt-5 space-y-3 max-h-[480px] overflow-y-auto pr-1">
          {items.map(m => <ContactCard key={m.contact_id} m={m} onUpdate={onUpdate} />)}
        </ul>
      )}
    </div>
  );
};
